import React, { useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { useCreateContract } from "../../api/contracts";
import { useApplications } from "../../api/applications";
import { useCustomers } from "../../api/customers";
import { useDebounce } from "../../hooks/useDebounce";
import { useToast } from "../../components/ToastProvider";

export function NewContractModal({ show, onClose }: { show: boolean; onClose: () => void }) {
  const [customerQuery, setCustomerQuery] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [planId, setPlanId] = useState("");
  const [billingDay, setBillingDay] = useState(10);
  const [readjustmentIndex, setReadjustmentIndex] = useState<"IPCA" | "IGPM" | "NONE">("NONE");
  const [error, setError] = useState<string | null>(null);

  const debouncedCustomerQuery = useDebounce(customerQuery);
  const { data: customersData } = useCustomers({ pageSize: 20, q: debouncedCustomerQuery || undefined });
  const { data: appsData } = useApplications({ pageSize: 100 });
  const plans = useMemo(
    () => (appsData?.data ?? []).flatMap((app) => app.plans.map((p) => ({ ...p, appName: app.name }))),
    [appsData]
  );

  const createContract = useCreateContract();
  const toast = useToast();

  function reset() {
    setCustomerQuery("");
    setCustomerId("");
    setPlanId("");
    setBillingDay(10);
    setReadjustmentIndex("NONE");
    setError(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!customerId) return setError("Selecione o cliente.");
    if (!planId) return setError("Selecione a aplicação/plano.");
    try {
      await createContract.mutateAsync({ customerId: Number(customerId), planId: Number(planId), billingDay, readjustmentIndex });
      toast("Contrato criado.");
      reset();
      onClose();
    } catch {
      setError("Não foi possível criar o contrato.");
    }
  }

  return (
    <Modal backdrop="static" keyboard={false} show={show} onHide={onClose} centered className="nt-dialog" size="lg">
      <form onSubmit={handleSubmit}>
        <Modal.Header closeButton closeVariant="white">
          <Modal.Title style={{ fontSize: 20, fontFamily: "var(--font-heading)" }}>Novo contrato</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row g-3">
            <div className="col-12">
              <label className="form-label" style={{ fontSize: 12 }}>
                Cliente
              </label>
              <input
                className="form-control mb-2"
                placeholder="Buscar cliente…"
                value={customerQuery}
                onChange={(e) => setCustomerQuery(e.target.value)}
              />
              <select className="form-select" value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
                <option value="">Selecione…</option>
                {customersData?.data.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.razaoSocial} — {c.cnpjCpf}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-sm-6">
              <label className="form-label" style={{ fontSize: 12 }}>
                Aplicação / plano
              </label>
              <select className="form-select" value={planId} onChange={(e) => setPlanId(e.target.value)}>
                <option value="">Selecione…</option>
                {plans.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.appName} — {p.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-sm-3">
              <label className="form-label" style={{ fontSize: 12 }}>
                Dia de cobrança
              </label>
              <input
                type="number"
                min={1}
                max={28}
                className="form-control"
                value={billingDay}
                onChange={(e) => setBillingDay(Number(e.target.value))}
              />
            </div>
            <div className="col-12 col-sm-3">
              <label className="form-label" style={{ fontSize: 12 }}>
                Reajuste
              </label>
              <select
                className="form-select"
                value={readjustmentIndex}
                onChange={(e) => setReadjustmentIndex(e.target.value as "IPCA" | "IGPM" | "NONE")}
              >
                <option value="NONE">Nenhum</option>
                <option value="IPCA">IPCA</option>
                <option value="IGPM">IGP-M</option>
              </select>
            </div>
          </div>
          {error && (
            <div className="text-danger mt-2" style={{ fontSize: 12.5 }}>
              {error}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-danger" onClick={onClose}>
            Cancelar
          </button>
          <button type="submit" className="btn btn-success" disabled={createContract.isPending}>
            Criar contrato
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
